import React, { useEffect, useState, createContext, useContext } from 'react';
import { AdminOrder, MOCK_ORDERS } from '../data/adminMockData';
import { Product, products as initialProducts } from '../data/products';
export interface CustomerAccount {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addresses: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }[];
}
export interface DiscountCode {
  code: string;
  description: string;
  type: 'percentage' | 'fixed';
  value: number;
  active: boolean;
  minSubtotal?: number;
  maxDiscount?: number;
  usageLimit?: number;
  timesUsed: number;
  startDate?: string;
  endDate?: string;
  appliesToType?: 'all' | 'group' | 'category' | 'product';
  appliesToValue?: string;
}
interface StoreContextType {
  orders: AdminOrder[];
  customers: CustomerAccount[];
  discounts: DiscountCode[];
  products: Product[];
  currentUser: CustomerAccount | null;
  addOrder: (order: AdminOrder) => void;
  updateOrderStatus: (orderId: string, status: AdminOrder['status']) => void;
  updateOrder: (orderId: string, updates: Partial<AdminOrder>) => void;
  addCustomer: (customer: CustomerAccount) => void;
  login: (email: string) => void;
  logout: () => void;
  addDiscount: (discount: DiscountCode) => void;
  updateDiscount: (code: string, discount: Partial<DiscountCode>) => void;
  deleteDiscount: (code: string) => void;
  validateDiscount: (code: string, subtotal: number, items?: {
    product: Product;
    quantity: number;
  }[]) => {
    valid: boolean;
    amount: number;
    error?: string;
  };
  updateProducts: (newProducts: Product[]) => void;
}
const StoreContext = createContext<StoreContextType | undefined>(undefined);
const STORAGE_KEYS = {
  ORDERS: 'yarbo_store_orders',
  CUSTOMERS: 'yarbo_store_customers',
  DISCOUNTS: 'yarbo_store_discounts',
  CURRENT_USER: 'yarbo_store_current_user',
  PRODUCTS: 'yarbo_store_products'
};
const DEFAULT_DISCOUNTS: DiscountCode[] = [{
  code: 'SAVE100',
  description: '$100 off orders over $1,000',
  type: 'fixed',
  value: 100,
  active: true,
  minSubtotal: 1000,
  timesUsed: 0
}, {
  code: 'YARBO10',
  description: '10% off your entire order',
  type: 'percentage',
  value: 10,
  active: true,
  timesUsed: 0
}];
export function StoreProvider({
  children
}: {
  children: ReactNode;
}) {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [customers, setCustomers] = useState<CustomerAccount[]>([]);
  const [discounts, setDiscounts] = useState<DiscountCode[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [currentUser, setCurrentUser] = useState<CustomerAccount | null>(null);
  // Load initial data
  useEffect(() => {
    const loadData = () => {
      const storedOrders = localStorage.getItem(STORAGE_KEYS.ORDERS);
      if (storedOrders) {
        setOrders(JSON.parse(storedOrders));
      } else {
        setOrders(MOCK_ORDERS);
        localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(MOCK_ORDERS));
      }
      const storedCustomers = localStorage.getItem(STORAGE_KEYS.CUSTOMERS);
      if (storedCustomers) {
        setCustomers(JSON.parse(storedCustomers));
      } else {
        const mockCustomers = MOCK_ORDERS.map(o => ({
          id: `CUST-${Math.floor(Math.random() * 10000)}`,
          firstName: o.customer.firstName,
          lastName: o.customer.lastName,
          email: o.customer.email,
          phone: o.customer.phone,
          addresses: [o.shipping]
        }));
        const uniqueCustomers = Array.from(new Map(mockCustomers.map(c => [c.email, c])).values());
        setCustomers(uniqueCustomers);
        localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify(uniqueCustomers));
      }
      const storedDiscounts = localStorage.getItem(STORAGE_KEYS.DISCOUNTS);
      if (storedDiscounts) {
        setDiscounts(JSON.parse(storedDiscounts));
      } else {
        setDiscounts(DEFAULT_DISCOUNTS);
        localStorage.setItem(STORAGE_KEYS.DISCOUNTS, JSON.stringify(DEFAULT_DISCOUNTS));
      }
      const storedProducts = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      if (storedProducts) {
        setProducts(JSON.parse(storedProducts));
      } else {
        setProducts(initialProducts);
        localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(initialProducts));
      }
      const storedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
      if (storedUser) setCurrentUser(JSON.parse(storedUser));
    };
    loadData();
  }, []);
  const addOrder = (order: AdminOrder) => {
    setOrders(prev => {
      const newOrders = [order, ...prev];
      localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(newOrders));
      return newOrders;
    });
    if (order.discountCode) {
      setDiscounts(prev => {
        const newDiscounts = prev.map(d => d.code.toUpperCase() === order.discountCode?.toUpperCase() ? {
          ...d,
          timesUsed: d.timesUsed + 1
        } : d);
        localStorage.setItem(STORAGE_KEYS.DISCOUNTS, JSON.stringify(newDiscounts));
        return newDiscounts;
      });
    }
  };
  const updateOrderStatus = (orderId: string, status: AdminOrder['status']) => {
    setOrders(prev => {
      const newOrders = prev.map(o => o.id === orderId ? {
        ...o,
        status
      } : o);
      localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(newOrders));
      return newOrders;
    });
  };
  const updateOrder = (orderId: string, updates: Partial<AdminOrder>) => {
    setOrders(prev => {
      const newOrders = prev.map(o => o.id === orderId ? {
        ...o,
        ...updates
      } : o);
      localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(newOrders));
      return newOrders;
    });
  };
  const addCustomer = (customer: CustomerAccount) => {
    setCustomers(prev => {
      const newCustomers = [...prev, customer];
      localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify(newCustomers));
      return newCustomers;
    });
  };
  const login = (email: string) => {
    const user = customers.find(c => c.email.toLowerCase() === email.toLowerCase());
    if (user) {
      setCurrentUser(user);
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
    } else {
      // Auto-create for demo purposes
      const newUser: CustomerAccount = {
        id: `CUST-${Math.floor(Math.random() * 10000)}`,
        firstName: 'Demo',
        lastName: 'User',
        email,
        phone: '',
        addresses: []
      };
      addCustomer(newUser);
      setCurrentUser(newUser);
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(newUser));
    }
  };
  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
  };
  const addDiscount = (discount: DiscountCode) => {
    setDiscounts(prev => {
      const newDiscounts = [...prev, discount];
      localStorage.setItem(STORAGE_KEYS.DISCOUNTS, JSON.stringify(newDiscounts));
      return newDiscounts;
    });
  };
  const updateDiscount = (code: string, updates: Partial<DiscountCode>) => {
    setDiscounts(prev => {
      const newDiscounts = prev.map(d => d.code === code ? {
        ...d,
        ...updates
      } : d);
      localStorage.setItem(STORAGE_KEYS.DISCOUNTS, JSON.stringify(newDiscounts));
      return newDiscounts;
    });
  };
  const deleteDiscount = (code: string) => {
    setDiscounts(prev => {
      const newDiscounts = prev.filter(d => d.code !== code);
      localStorage.setItem(STORAGE_KEYS.DISCOUNTS, JSON.stringify(newDiscounts));
      return newDiscounts;
    });
  };
  const validateDiscount = (code: string, subtotal: number, items?: {
    product: Product;
    quantity: number;
  }[]) => {
    const discount = discounts.find(d => d.code.toUpperCase() === code.toUpperCase());
    if (!discount) return {
      valid: false,
      amount: 0,
      error: 'Invalid discount code'
    };
    if (!discount.active) return {
      valid: false,
      amount: 0,
      error: 'Discount code is inactive'
    };
    const now = new Date().toISOString();
    if (discount.startDate && now < discount.startDate) {
      return {
        valid: false,
        amount: 0,
        error: 'Discount code is not yet active'
      };
    }
    if (discount.endDate && now > discount.endDate) {
      return {
        valid: false,
        amount: 0,
        error: 'Discount code has expired'
      };
    }
    if (discount.usageLimit && discount.timesUsed >= discount.usageLimit) {
      return {
        valid: false,
        amount: 0,
        error: 'Discount code usage limit reached'
      };
    }
    if (discount.minSubtotal && subtotal < discount.minSubtotal) {
      return {
        valid: false,
        amount: 0,
        error: `Minimum order amount is $${discount.minSubtotal}`
      };
    }
    let applicableSubtotal = subtotal;
    if (items && discount.appliesToType && discount.appliesToType !== 'all') {
      applicableSubtotal = items.reduce((sum, item) => {
        let applies = false;
        if (discount.appliesToType === 'group' && item.product.primaryGroup === discount.appliesToValue) applies = true;
        if (discount.appliesToType === 'category' && (item.product.category === discount.appliesToValue || item.product.publicCategories?.includes(discount.appliesToValue || ''))) applies = true;
        if (discount.appliesToType === 'product' && item.product.sku === discount.appliesToValue) applies = true;
        return sum + (applies ? item.product.price * item.quantity : 0);
      }, 0);
      if (applicableSubtotal === 0) {
        return {
          valid: false,
          amount: 0,
          error: 'Discount code does not apply to items in your cart'
        };
      }
    }
    let amount = 0;
    if (discount.type === 'fixed') {
      amount = discount.value;
    } else if (discount.type === 'percentage') {
      amount = applicableSubtotal * (discount.value / 100);
    }
    if (discount.maxDiscount && amount > discount.maxDiscount) {
      amount = discount.maxDiscount;
    }
    return {
      valid: true,
      amount
    };
  };
  const updateProducts = (newProducts: Product[]) => {
    setProducts(newProducts);
    localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(newProducts));
  };
  return <StoreContext.Provider value={{
    orders,
    customers,
    discounts,
    products,
    currentUser,
    addOrder,
    updateOrderStatus,
    updateOrder,
    addCustomer,
    login,
    logout,
    addDiscount,
    updateDiscount,
    deleteDiscount,
    validateDiscount,
    updateProducts
  }} data-id="element-315">
      {children}
    </StoreContext.Provider>;
}
export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}